import React from 'react';

import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {useNavigate} from 'react-router-dom';

import {useTenantInfo} from 'modules/tenant/hooks';
import {formatAxiosErrorOrThrow} from 'shared/utils/error';
import {ICreateUserForm} from 'users/pages/CreateUser/CreateUser';
import {createUserFromSignupPage} from 'modules/user/api/user';
import {ISignupPageSummary} from 'modules/signup-page/models';

import {FullScreenModal} from 'shared/components/FullScreenModal/FullScreenModal';
import {FullScreenModalTitle} from 'shared/components/FullScreenModal/FullScreenModalTitle';
import {PublicSignupForm} from 'shared/pages/Signup/StudentSignup/PublicSignupForm';

import '../styles.scss';

interface IStudentFreeSignupProps {
    signupPageSummary: ISignupPageSummary;
}

export const StudentFreeSignup = ({signupPageSummary}: IStudentFreeSignupProps) => {
    const {t} = useTranslation();
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

    const {tenantInfo} = useTenantInfo();

    const navigate = useNavigate();

    const onSubmit = ({role, ...userCreate}: ICreateUserForm) => {
        if (isSubmitting) {
            return;
        }
        setIsSubmitting(true);
        (async () => {
            try {
                await createUserFromSignupPage(
                    tenantInfo.id,
                    signupPageSummary.id,
                    {
                        roles: [role],
                        ...userCreate,
                    },
                );
                // redirect to login page
                navigate('/login');
                toast.success(t('signup.success', 'Signup successful! Please login'));
            } catch (e) {
                toast.error(formatAxiosErrorOrThrow(t(
                    'signup.submit.error', 'There was an error',
                ), e));
            } finally {
                setIsSubmitting(false);
            }
        })();
    };

    return (
        <FullScreenModal close={false} size="md">
            <FullScreenModalTitle className="mb-4">
                {t(
                    'common.parentSignup.title', 'Sign up as a public user to Young Eyes {{tenantName}}',
                    {tenantName: tenantInfo.name},
                )}
            </FullScreenModalTitle>
            <PublicSignupForm isSubmitting={isSubmitting} onSubmit={onSubmit} />
        </FullScreenModal>
    );
};